/* Linden — cart behavior.

   Adds, updates and removes lines through Shopify's Cart AJAX API. Every form
   still works as a plain POST if this script never runs.

   One mutation, one request. add.js and change.js both accept a `sections`
   parameter and return the rendered HTML alongside the new cart, so the markup
   that comes back describes the same instant as the change that caused it.

   This used to be four requests for a single press of +: change.js, then the
   section rendering API for the drawer and header, then again for the cart page
   section, then /cart.js to relabel the product cards. Four renders taken at
   four different moments, stitched together — and whenever a second press
   landed in the middle, the answers arrived describing carts that no longer
   existed. That is what "ça marche mais des fois ça bug" was.

   Documented caveat, honoured below: a section that fails to render comes back
   as null rather than as an error. */

const routes = window.Shopify?.routes?.root || '/';

/* At most five may be asked for; this page needs three at the outside. */
function sectionIds() {
  const ids = ['cart-drawer', 'header'];
  const page = document.querySelector('[data-cart-page-section]');
  if (page) ids.push(page.dataset.cartPageSection);
  return ids;
}

/* Parsed against this document rather than through DOMParser, whose document
   has no custom element registry of its own. */
const parse = (html) => document.createRange().createContextualFragment(html);

const announce = (message) => {
  const region = document.getElementById('live-region');
  if (region && message) region.textContent = message;
};

/* — feedback ————————————————————————————————————

   Shopify rejects an add for reasons the customer can act on — sold out, a
   quantity beyond stock. announce() alone puts that in the live region, where a
   sighted customer never sees it: the button flickers, nothing moves, and the
   shop reads as broken rather than the item as unavailable. Say it where the
   click happened, one line per form rather than a growing stack. */
function showError(form, message) {
  if (!message) return;

  let box = form.querySelector('[data-add-error]');
  if (!box) {
    box = document.createElement('p');
    box.className = 'field__error';
    box.setAttribute('data-add-error', '');
    box.setAttribute('role', 'status');
    (form.querySelector('[type="submit"]') || form).after(box);
  }

  box.textContent = message;
  announce(message);
}

const clearError = (form) => form.querySelector('[data-add-error]')?.remove();

/* — painting ———————————————————————————————————— */

/* Which control the customer was on, so it can be handed back afterwards.
   Re-rendering destroys the button under their finger, and on the second tap of
   a quick series there is nothing left to hit. */
function focusMemo() {
  const active = document.activeElement;
  const row = active?.closest?.('[data-cart-item]');
  if (!row) return null;

  return {
    key: row.dataset.cartItem,
    change: active.dataset.quantityChange || null,
    remove: active.hasAttribute('data-cart-remove'),
  };
}

function focusRestore(memo) {
  if (!memo) return;

  const row = document.querySelector(`[data-cart-item="${CSS.escape(memo.key)}"]`);
  if (!row) return;

  const target = memo.remove
    ? row.querySelector('[data-cart-remove]')
    : row.querySelector(`[data-quantity-change="${memo.change}"]`);

  target?.focus();
}

function applySections(sections) {
  if (!sections) return;

  const memo = focusMemo();

  const drawerHtml = sections['cart-drawer'];
  if (drawerHtml) {
    const fresh = parse(drawerHtml).querySelector('#cart-drawer');
    const current = document.querySelector('#cart-drawer');

    if (fresh && current) {
      const wasOpen = current.hasAttribute('open');
      const opener = current.opener;

      current.replaceWith(fresh);
      if (wasOpen) fresh.open(opener);
    }
  }

  if (sections.header) {
    const fragment = parse(sections.header);
    const pairs = [
      ['[data-cart-count]', fragment.querySelector('[data-cart-count]')],
      ['[data-cart-count-text]', fragment.querySelector('[data-cart-count-text]')],
    ];

    for (const [selector, fresh] of pairs) {
      const current = document.querySelector(selector);
      if (fresh && current) current.textContent = fresh.textContent;
    }
  }

  const host = document.querySelector('[data-cart-page-section]');
  if (host) {
    const html = sections[host.dataset.cartPageSection];
    if (html) {
      const fresh = parse(html).querySelector('[data-cart-page-section]');
      if (fresh) host.replaceWith(fresh);
    }
  }

  focusRestore(memo);
}

/* A card for something already in the cart should not offer the same words as a
   card for something never chosen — "Ajouter au panier" twice reads as if the
   first click did nothing. It says "Ajouter un autre" instead.

   Only the [data-add-label] span is written to. The button also holds a
   visually-hidden product name, and writing the button's own textContent would
   fold the two into one string and print the hidden half on the card.

   Reflected here rather than in Liquid because collection pages are cached and
   served to every visitor alike; a label baked server-side would show one
   shopper's cart to the next. The cart is passed in after a mutation, since the
   response already carries it — asking again would be both a spare request and
   a second, later answer. */
async function reflectCart(cart) {
  const buttons = document.querySelectorAll('[data-add-to-cart][data-variant-id]');
  if (!buttons.length) return;

  try {
    const source = cart || (await (await fetch(`${routes}cart.js`, {
      headers: { Accept: 'application/json' },
    })).json());

    const held = new Set((source.items || []).map((item) => String(item.variant_id)));

    for (const button of buttons) {
      const label = button.querySelector('[data-add-label]');
      if (!label) continue;

      if (!button.dataset.labelAdd) button.dataset.labelAdd = label.textContent.trim();

      const next = held.has(String(button.dataset.variantId))
        ? button.dataset.labelAgain
        : button.dataset.labelAdd;

      if (next && label.textContent !== next) label.textContent = next;
    }
  } catch (error) {
    // Labels stay as rendered, and every button still adds.
  }
}

/* — mutations ————————————————————————————————————

   Every cart change runs through here, one at a time. Two overlapping calls can
   answer out of order, and the late one would repaint a quantity the customer
   has already moved past. */
let queue = Promise.resolve();

const serialise = (job) => {
  queue = queue.then(job, job);
  return queue;
};

async function mutate(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      ...body,
      sections: sectionIds().join(','),
      /* Without this the sections render against the Referer, which is the page
         the customer happens to be on. The cart page section has to be rendered
         in the cart's own context or it comes back describing another page. */
      sections_url: window.location.pathname,
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || '');

  applySections(data.sections);
  reflectCart(data);
  return data;
}

const changeLine = (id, quantity) =>
  mutate(`${routes}cart/change.js`, { id: String(id), quantity });

/* — add to cart ————————————————————————————————— */

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[action*="/cart/add"]');
  if (!form) return;

  /* data-plain opts a form out and lets the browser post it normally. */
  if (form.hasAttribute('data-plain')) return;

  event.preventDefault();
  clearError(form);

  const button = form.querySelector('[type="submit"]');

  /* innerHTML, never textContent. These buttons hold a visually-hidden span
     with the product name, so a screen reader hears "Ajouter au panier — Le Kit
     Comprendre & S'apaiser" while the page shows only the first half.
     textContent reads straight through that span and returns both halves as one
     string; assigning it back replaces the span with a plain text node. */
  const original = button?.innerHTML;

  if (button) {
    button.setAttribute('aria-disabled', 'true');
    if (button.dataset.addingLabel) button.textContent = button.dataset.addingLabel;
  }

  try {
    /* "Buy it with" ticks travel with the main product in one request, so the
       cart never shows a half-added bundle if the second call fails.

       Never `form.id` here. HTMLFormElement carries [LegacyOverrideBuiltIns],
       so a control named "id" — which every product form has, holding the
       variant — shadows the element's own id property. Reading it returns the
       input rather than the string, the attribute selector matches nothing, and
       every ticked add-on is dropped without an error anywhere. */
    const bundle =
      form.querySelector('buy-it-with') ||
      document.querySelector(`buy-it-with[data-form="${form.getAttribute('id')}"]`);
    const extras = bundle ? bundle.selection : [];

    const variantId = form.querySelector('[name="id"]')?.value;
    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);

    const items = [{ id: Number(variantId), quantity }, ...extras];

    await serialise(() => mutate(`${routes}cart/add.js`, { items }));

    announce(button?.dataset.addedLabel || '');

    const drawer = document.querySelector('#cart-drawer');
    if (drawer && drawer.dataset.autoOpen === 'true') drawer.open(button);
  } catch (error) {
    if (error instanceof TypeError) {
      form.submit(); // the network is gone; let the browser do it
      return;
    }
    showError(form, error.message);
  } finally {
    if (button) {
      button.removeAttribute('aria-disabled');
      if (original != null) button.innerHTML = original;
    }
  }
});

/* — quantity and removal ————————————————————————

   One request per line per pause. A click used to mean a round trip plus a
   repaint, so holding + fired a request per press and rebuilt the cart under
   the cursor each time. Waiting 300 ms collapses a burst into the figure they
   actually stopped on. The digit itself moves at once — quantity-selector
   updates the input locally — so the wait reads as the totals catching up. */
const pendingQuantity = new Map();

function scheduleQuantity(key, quantity, row) {
  row?.classList.add('is-busy');
  clearTimeout(pendingQuantity.get(key));

  pendingQuantity.set(key, setTimeout(() => {
    pendingQuantity.delete(key);

    serialise(async () => {
      try {
        await changeLine(key, quantity);
      } catch (error) {
        window.location.href = `${routes}cart`;
      }
    });
  }, 300));
}

document.addEventListener('change', (event) => {
  const input = event.target.closest('.quantity__input');
  if (!input) return;

  const line = input.closest('[data-cart-item]');
  if (!line) return;

  scheduleQuantity(line.dataset.cartItem, Number(input.value), line);
});

document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-cart-remove]');
  if (!remove) return;

  event.preventDefault();
  remove.closest('[data-cart-item]')?.classList.add('is-busy');

  /* A pending quantity change for this line would otherwise fire after the
     removal and put the line back. */
  clearTimeout(pendingQuantity.get(remove.dataset.cartRemove));
  pendingQuantity.delete(remove.dataset.cartRemove);

  serialise(async () => {
    try {
      await changeLine(remove.dataset.cartRemove, 0);
      announce(remove.dataset.removedLabel || '');
    } catch (error) {
      window.location.href = `${routes}cart`;
    }
  });
});

/* buy-it-with adds and removes on its own in instant mode, and asks here for
   the rest of the page to catch up. One request, current page as context. */
document.addEventListener('cart:refresh', () => {
  serialise(async () => {
    try {
      const url = `${window.location.pathname}?sections=${sectionIds().join(',')}`;
      const response = await fetch(url);
      if (!response.ok) return;

      applySections(await response.json());
      reflectCart();
    } catch (error) {
      // The page keeps what it shows; a reload still tells the truth.
    }
  });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => reflectCart());
} else {
  reflectCart();
}
